import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ValidationError } from 'yup';
import { useFlowerTypeOptions, useAssemblyTypeOptions, useColorOptions } from '../../hooks/useProducts';
import { useCategoriesGroupedBySection } from '@modules/categories';
import { productFormSchema, PRODUCT_DESCRIPTION_MAX_LENGTH } from '../../utils/productValidation';
import {
  Button,
  ControlledInput,
  ControlledTextarea,
  ControlledFileInput,
  ControlledSelect,
  ControlledMultiSelect,
  ControlledCompositionInput,
} from '@shared/ui';
// import { ControlledKeywordsInput } from '../KeywordsInput';
import { ControlledCategorySelection, type CategorySelectionItem } from '../CategorySelection';
import type { CompositionItem } from '@shared/ui';
import './ProductForm.css';

const NOT_FLOWER_HINT =
  'Категория без цветов: тип цветка и тип сборки не нужны. Цвет можно указать.';

interface ProductFormData {
  categorySelections?: CategorySelectionItem[];
  name: string;
  description: string;
  price: number;
  composition?: CompositionItem[];
  keywords?: string[];
  height?: number;
  width?: number;
  weight?: number;
  flowerCount?: number | null;
  isNotFlower?: boolean;
  flowerTypeIds?: number[];
  assemblyTypeId?: number | null;
  assemblyTimeMinutes?: number | null;
  colorIds?: number[];
  images?: (File | { id: number; url: string })[];
}

interface ProductFormProps {
  onSubmit?: (data: ProductFormData) => void | Promise<void>;
  initialValues?: Partial<ProductFormData>;
  isEdit?: boolean;
  isLoading?: boolean;
  /** Пошаговый режим (мобильное создание товара) */
  wizard?: boolean;
}

const WIZARD_STEPS = [
  {
    id: 'basics',
    title: 'Фото и основное',
    fields: ['images', 'name', 'price'] as const,
  },
  {
    id: 'category',
    title: 'Категория',
    fields: ['categorySelections', 'description'] as const,
  },
  {
    id: 'composition',
    title: 'Состав и размеры',
    fields: ['composition', 'height', 'width', 'weight', 'assemblyTimeMinutes'] as const,
  },
  {
    id: 'attrs',
    title: 'Характеристики',
    fields: [
      'isNotFlower',
      'flowerCount',
      'flowerTypeIds',
      'assemblyTypeId',
      'colorIds',
    ] as const,
  },
  // Ключевые слова скрыты: поиск идёт по названию и описанию.
  // {
  //   id: 'keywords',
  //   title: 'Ключевые слова',
  //   fields: ['keywords'] as const,
  // },
] as const;

const ALL_WIZARD_FIELDS = WIZARD_STEPS.flatMap((s) => [...s.fields]);

export default function ProductForm({
  onSubmit: onSubmitProp,
  initialValues,
  isEdit = false,
  isLoading,
  wizard = false,
}: ProductFormProps = {}) {
  const resolver = yupResolver(productFormSchema) as any;
  const [step, setStep] = useState(0);
  // Показывать ошибки только после «Далее» / «Создать» на текущем шаге.
  const [showErrors, setShowErrors] = useState(!wizard);

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    setValue,
    setError,
    watch,
    clearErrors,
    trigger,
  } = useForm<ProductFormData>({
    resolver,
    // До первой ошибки на шаге — не валидируем на каждый ввод.
    // После показа ошибок — onChange, чтобы они снимались при заполнении.
    mode: wizard && !showErrors ? 'onSubmit' : 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ...(initialValues || {
        categorySelections: [{ sectionId: '', categoryId: '' }],
        name: '',
        description: '',
        price: undefined,
        composition: [{ value: '', count: 0 }],
        keywords: [],
        height: undefined,
        width: undefined,
        weight: undefined,
        flowerCount: undefined,
        isNotFlower: false,
        flowerTypeIds: [],
        assemblyTypeId: null,
        assemblyTimeMinutes: undefined,
        colorIds: [],
        images: [],
      }),
    },
  });

  const flowerTypeOptions = useFlowerTypeOptions();
  const assemblyTypeOptions = useAssemblyTypeOptions();
  const colorOptions = useColorOptions();
  const { data: categoriesGroupedBySection = {} } = useCategoriesGroupedBySection();

  const isNotFlower = watch('isNotFlower') || false;
  const categorySelections = watch('categorySelections');
  const descriptionValue = watch('description') || '';
  const categoriesReady = Object.keys(categoriesGroupedBySection).length > 0;
  const notFlowerFromCategory = useMemo(() => {
    if (!categoriesReady) return isNotFlower;
    return categorySelectionsAreNotFlower(categorySelections, categoriesGroupedBySection);
  }, [categoriesReady, isNotFlower, categorySelections, categoriesGroupedBySection]);

  useEffect(() => {
    if (isNotFlower === notFlowerFromCategory) return;
    setValue('isNotFlower', notFlowerFromCategory);
  }, [isNotFlower, notFlowerFromCategory, setValue]);

  useEffect(() => {
    if (!wizard) return;
    setShowErrors(false);
    clearErrors(ALL_WIZARD_FIELDS as (keyof ProductFormData)[]);
  }, [step, wizard, clearErrors]);

  // setError из валидации шага не включает isSubmitted — сами перепроверяем поле при изменении.
  useEffect(() => {
    if (!wizard || !showErrors) return;
    const stepFields = new Set<string>(WIZARD_STEPS[step].fields);
    const subscription = watch((_values, info) => {
      if (info.type !== 'change' || !info.name) return;
      const root = info.name.split('.')[0];
      if (!stepFields.has(root)) return;
      void trigger(root as keyof ProductFormData);
      // «Не цветы» влияет на обязательность полей характеристик
      if (root === 'isNotFlower') {
        void trigger(['flowerTypeIds', 'assemblyTypeId', 'colorIds']);
      }
    });
    return () => subscription.unsubscribe();
  }, [wizard, showErrors, step, watch, trigger]);

  useEffect(() => {
    if (isNotFlower) {
      setValue('flowerTypeIds', []);
      setValue('assemblyTypeId', null);
      setValue('flowerCount', null);
      clearErrors(['flowerTypeIds', 'assemblyTypeId', 'colorIds']);
    }
  }, [isNotFlower, setValue, clearErrors]);

  const onSubmit = async (data: ProductFormData) => {
    if (onSubmitProp) {
      await onSubmitProp(data);
    }
  };

  const onError = (formErrors: unknown) => {
    if (wizard) setShowErrors(true);
    console.log('Ошибки валидации:', formErrors);
    console.log('Данные формы (с ошибками):', getValues());
  };

  /** Валидируем только поля текущего шага (не всю схему — иначе ошибки 4-го шага копятся заранее). */
  const validateCurrentStep = async () => {
    const fields = [...WIZARD_STEPS[step].fields] as Array<keyof ProductFormData>;
    clearErrors(fields);
    const values = getValues();
    try {
      await (productFormSchema.pick(fields as any) as typeof productFormSchema).validate(values, {
        abortEarly: false,
      });
      return true;
    } catch (err) {
      if (err instanceof ValidationError) {
        const seen = new Set<string>();
        for (const e of err.inner.length ? err.inner : [err]) {
          const path = e.path;
          if (!path || seen.has(path)) continue;
          seen.add(path);
          setError(path as keyof ProductFormData, {
            type: e.type || 'validation',
            message: e.message,
          });
        }
      }
      return false;
    }
  };

  const goNext = async () => {
    const ok = await validateCurrentStep();
    if (!ok) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    clearErrors(ALL_WIZARD_FIELDS as (keyof ProductFormData)[]);
    // Откладываем смену шага: иначе тот же click попадает в type="submit" и сабмитит форму.
    window.setTimeout(() => {
      setStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
    }, 0);
  };

  const goBack = () => {
    setShowErrors(false);
    clearErrors(ALL_WIZARD_FIELDS as (keyof ProductFormData)[]);
    setStep((s) => Math.max(s - 1, 0));
  };

  const submitWizard = () => {
    void handleSubmit(onSubmit, onError)();
  };

  const isLastStep = step === WIZARD_STEPS.length - 1;
  const showStep = (index: number) => !wizard || step === index;
  const fieldShowError = !wizard || showErrors;

  const categoryAndMain = (
    <>
      {showStep(1) ? (
        <div className="form-group">
          <ControlledCategorySelection
            name="categorySelections"
            control={control}
            error={errors.categorySelections as any}
          />
        </div>
      ) : null}

      {showStep(0) ? (
        <div className="form-group">
          <ControlledInput
            id="name"
            type="text"
            label="Название *"
            name="name"
            control={control}
            error={errors.name}
          />
        </div>
      ) : null}

      {showStep(1) ? (
        <div className="form-group form-group--description">
          <ControlledTextarea
            id="description"
            rows={4}
            label="Описание"
            name="description"
            control={control}
            error={errors.description}
            maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
            styles={{ input: { paddingBottom: 22 } }}
          />
          <span className="description-counter">
            {descriptionValue.length}/{PRODUCT_DESCRIPTION_MAX_LENGTH}
          </span>
        </div>
      ) : null}

      {showStep(0) ? (
        <div className="form-group">
          <ControlledInput
            id="price"
            type="number"
            step="0.01"
            min="0"
            label="Цена *"
            name="price"
            control={control}
            error={errors.price}
            emptyNumberValue={null}
          />
        </div>
      ) : null}

      {showStep(2) ? (
        <>
          <div className="form-group">
            <ControlledCompositionInput
              name="composition"
              control={control}
              error={errors.composition as any}
              showError={fieldShowError}
              label="Состав *"
              description="Укажите все части букета: цветы, ленту, упаковку и т.п."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <ControlledInput
                id="height"
                type="number"
                step="0.01"
                min="0"
                label="Высота (см) *"
                name="height"
                control={control}
                error={errors.height}
                emptyNumberValue={null}
              />
            </div>
            <div className="form-group">
              <ControlledInput
                id="width"
                type="number"
                step="0.01"
                min="0"
                label="Ширина (см) *"
                name="width"
                control={control}
                error={errors.width}
                emptyNumberValue={null}
              />
            </div>
          </div>

          <div className="form-group">
            <ControlledInput
              id="weight"
              type="number"
              step="1"
              min="0"
              max="15000"
              label="Вес (г) *"
              name="weight"
              control={control}
              error={errors.weight}
              emptyNumberValue={null}
            />
          </div>

          <div className="form-group">
            <ControlledInput
              id="assemblyTimeMinutes"
              type="number"
              step="1"
              min="0"
              label="Время на сборку (мин)"
              name="assemblyTimeMinutes"
              control={control}
              error={errors.assemblyTimeMinutes}
              emptyNumberValue={null}
            />
            <p className="bouquet-fields-hint" style={{ marginTop: 6 }}>
              Сколько минут нужно на сборку. Пустое поле или 0 — сборка не требуется.
            </p>
          </div>
        </>
      ) : null}
    </>
  );

  const attrsAndImages = (
    <>
      {showStep(3) ? (
        <>
          <p className="bouquet-fields-hint bouquet-fields-hint--desktop">
            {isNotFlower
              ? NOT_FLOWER_HINT
              : 'Укажите тип цветка, тип сборки и цвет букета.'}
          </p>

          {!isNotFlower ? (
            <>
          <div className="form-group">
            <ControlledInput
              id="flowerCount"
              type="number"
              step="1"
              min="0"
              label="Общее кол-во цветов в букете"
              name="flowerCount"
              control={control}
              error={errors.flowerCount}
              showError={fieldShowError}
              emptyNumberValue={null}
            />
          </div>

          <div className="form-group">
            <ControlledMultiSelect
              id="flowerTypeIds"
              label="Типы цветков *"
              placeholder="Выберите типы цветков"
              data={flowerTypeOptions}
              name="flowerTypeIds"
              control={control}
              clearable
              searchable
              error={errors.flowerTypeIds as any}
              showError={fieldShowError}
            />
          </div>

          <div className="form-group">
            <ControlledSelect
              id="assemblyTypeId"
              label="Тип сборки *"
              placeholder="Выберите тип сборки"
              name="assemblyTypeId"
              control={control}
              data={assemblyTypeOptions}
              clearable
              nothingFound="Ничего не найдено"
              showError={fieldShowError}
            />
          </div>
            </>
          ) : null}

          <div className="form-group">
            <ControlledMultiSelect
              id="colorIds"
              label={isNotFlower ? 'Цвет' : 'Цвет букета *'}
              placeholder={isNotFlower ? 'Выберите цвет' : 'Выберите цвет букета'}
              data={colorOptions}
              name="colorIds"
              control={control}
              clearable
              searchable
              error={errors.colorIds as any}
              showError={fieldShowError}
            />
          </div>
        </>
      ) : null}

      {/* Ключевые слова скрыты, поле остаётся необязательным. */}
      {/* {showStep(4) ? (
        <div className="form-group">
          <div className="keywords-input-wrapper">
            <ControlledKeywordsInput
              name="keywords"
              control={control}
              label="Ключевые слова *"
              showError={fieldShowError}
            />
          </div>
        </div>
      ) : null} */}

      {showStep(0) ? (
        <div className="form-group product-form__images">
          <ControlledFileInput
            id="images"
            name="images"
            label="Изображения *(минимум 3, максимум 10)"
            description="Загружайте квадратные фото в формате 1:1 — так они корректно отображаются в каталоге и на карточке товара."
            reorderHint={
              isEdit
                ? 'Перетащите превью, чтобы изменить порядок. Смена только порядка не отправляет товар на модерацию.'
                : undefined
            }
            accept="image/*"
            multiple
            cropSquare
            control={control}
            error={errors.images}
          />
        </div>
      ) : null}
    </>
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      onKeyDown={(e) => {
        // Enter в input не должен сабмитить всю форму и подсвечивать 4-й шаг заранее
        if (
          wizard &&
          e.key === 'Enter' &&
          (e.target as HTMLElement).tagName !== 'TEXTAREA'
        ) {
          e.preventDefault();
        }
      }}
      className={`product-form${wizard ? ' product-form--wizard' : ''}${wizard && showErrors ? ' product-form--show-errors' : ''}${wizard && !showErrors ? ' product-form--hide-errors' : ''}`}
    >
      {wizard ? (
        <div className="product-form__wizard-top">
          <div className="product-form__steps" aria-label={isEdit ? 'Шаги редактирования товара' : 'Шаги создания товара'}>
            {WIZARD_STEPS.map((s, index) => (
              <div
                key={s.id}
                className={`product-form__step${index === step ? ' product-form__step--active' : ''}${index < step ? ' product-form__step--done' : ''}`}
              >
                <span className="product-form__step-num" aria-hidden>
                  {index < step ? '✓' : index + 1}
                </span>
                {index < WIZARD_STEPS.length - 1 ? (
                  <span className="product-form__step-line" aria-hidden />
                ) : null}
              </div>
            ))}
          </div>
          <p className="product-form__step-heading">
            <span className="product-form__step-heading-meta">
              Шаг {step + 1} из {WIZARD_STEPS.length}
            </span>
            {WIZARD_STEPS[step].title}
          </p>
        </div>
      ) : null}

      <div className={wizard ? 'product-form__wizard-body' : 'product-form__columns'}>
        {wizard ? (
          <div className="product-form__columns">
            <div className="product-form__panel">
              {categoryAndMain}
              {attrsAndImages}
            </div>
          </div>
        ) : (
          <>
            <div className="product-form__panel product-form__left">{categoryAndMain}</div>
            <div className="product-form__panel product-form__right">{attrsAndImages}</div>
          </>
        )}
      </div>

      <footer className="product-form__footer">
        {wizard ? (
          <>
            {step > 0 ? (
              <Button type="button" variant="secondary" onClick={goBack} disabled={isLoading}>
                Назад
              </Button>
            ) : (
              <span />
            )}
            {isLastStep ? (
              <Button
                type="button"
                loading={isLoading}
                className="product-form__submit-button"
                onClick={submitWizard}
              >
                {isEdit ? 'Сохранить' : 'Создать товар'}
              </Button>
            ) : (
              <Button type="button" onClick={() => void goNext()} disabled={isLoading}>
                Далее
              </Button>
            )}
          </>
        ) : (
          <Button type="submit" loading={isLoading} className="product-form__submit-button">
            {isEdit ? 'Обновить' : 'Создать товар'}
          </Button>
        )}
      </footer>
    </form>
  );
}

function categorySelectionsAreNotFlower(
  selections: CategorySelectionItem[] | undefined,
  grouped: Record<string, Array<{ id: number; isNotFlower?: boolean }> | undefined>,
): boolean {
  const ids = (selections ?? [])
    .map((item) => item.categoryId)
    .filter((id): id is number => typeof id === 'number');
  if (ids.length === 0) return false;
  const flags = new Map<number, boolean>();
  Object.values(grouped).forEach((list) => {
    list?.forEach((category) => {
      flags.set(category.id, category.isNotFlower === true);
    });
  });
  return ids.every((id) => flags.get(id) === true);
}
